import React, { useState, useEffect } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { Box, Drawer, List, ListItem, ListItemIcon, ListItemText, Typography, CssBaseline, Badge, GlobalStyles } from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SettingsIcon from '@mui/icons-material/Settings';
import MonitorIcon from '@mui/icons-material/Monitor';
import LogoutIcon from '@mui/icons-material/Logout';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ReceiptIcon from '@mui/icons-material/Receipt';
import BlockIcon from '@mui/icons-material/Block';
import SecurityIcon from '@mui/icons-material/Security';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import BarChartIcon from '@mui/icons-material/BarChart';
import CalculateIcon from '@mui/icons-material/Calculate';
import LockIcon from '@mui/icons-material/Lock';
import AppsIcon from '@mui/icons-material/Apps';
import PsychologyIcon from '@mui/icons-material/Psychology';
import KeyIcon from '@mui/icons-material/Key';
import axios from 'axios';

import LoginPage from './LoginPage';
import StatsPage from './pages/StatsPage';
import UsersPage from './pages/UsersPage';
import UserDetailPage from './pages/UserDetailPage';
import VideosPage from './pages/VideosPage';
import FinancePage from './pages/FinancePage';
import SystemPage from './pages/SystemPage';
import SettingsPage from './pages/SettingsPage';
import AvatarPage from './pages/AvatarPage';
import VideoEditorMusicPage from './pages/VideoEditorMusicPage';
import StudioMusicPage from './pages/StudioMusicPage';   // [D-506] музика заставки «Студії» (v-5)
import CostsPage from './pages/CostsPage';
import QueuePage from './pages/QueuePage';
import SupportPage from './pages/SupportPage';
import ReceiptsPage from './pages/ReceiptsPage';
import BlacklistPage from './pages/BlacklistPage';
import SecurityPage from './pages/SecurityPage';
import ExpensesPage from './pages/ExpensesPage';
import ReferralSettingsPage from './pages/ReferralSettingsPage';
import TrialRequestsPage from './pages/TrialRequestsPage';
import FakeUsersPage from './pages/FakeUsersPage';
import AntifraudPage from './pages/AntifraudPage';
import AntihackerPage from './pages/AntihackerPage';
import PlayLanguageSecurityPage from './pages/PlayLanguageSecurityPage';
import AnalystPage from './pages/AnalystPage';
import ContestsPage from './pages/ContestsPage';
import RetentionPage from './pages/RetentionPage';
import SecretsPage from './pages/SecretsPage';
import BackupPage from './pages/BackupPage';
import ReferralEarningsPage from './pages/ReferralEarningsPage';
import PlayLanguagePage from './pages/PlayLanguagePage';
import ZodiaqPage from './pages/ZodiaqPage';
import ChiromancyPage from './pages/ChiromancyPage';
import NumeriqPage from './pages/NumeriqPage';
import PackagesPage from './pages/PackagesPage';
// [D-321] керування посиленими ефектами
import HyperSettingsPage from './pages/HyperSettingsPage';
import PartnersPage from './pages/PartnersPage';   // [D-409]
import OurAppsPage from './pages/OurAppsPage';   // [D-420]
import { isDemo, setDemo, useDemo } from './demoMode';   // [D-423]
import AIMemoryPage from './pages/AIMemoryPage';
import SeamlessSessionPage from './pages/SeamlessSessionPage';
import AppInvitesPage from './pages/AppInvitesPage';   // [D-734]

const DRAWER_WIDTH = 220;

const DEFAULT_MENU = [
  { text: 'Статистика', icon: <DashboardIcon/>, path: '/' },
  { text: 'Черга', icon: <VideoLibraryIcon/>, path: '/queue' },
  { text: '🧪 Посилення', icon: <VideoLibraryIcon/>, path: '/hyper' },   // [D-321]
  { text: '🤝 Партнери', icon: <PeopleIcon/>, path: '/partners' },   // [D-409]
  { text: '🧩 Мої продукти', icon: <VideoLibraryIcon/>, path: '/our-apps' },   // [D-421]
  { text: 'Підтримка', icon: <SupportAgentIcon/>, path: '/support' },
  { text: 'Квитанції', icon: <ReceiptIcon/>, path: '/receipts' },
  { text: 'Чорний список', icon: <BlockIcon/>, path: '/blacklist' },
  { text: 'Безпека', icon: <SecurityIcon/>, children: [
    { text: '🔐 Секрети', path: '/secrets' },
    { text: '🛡 Загальна', path: '/security' },
    { text: '🛡 Антифрод', path: '/antifraud' },
    { text: '🛡 Антихакер', path: '/antihacker' },
    { text: '🛡 PlayLanguage', path: '/security/playlanguage' },
  ]},
  { text: 'Користувачі', icon: <PeopleIcon/>, path: '/users' },
  { text: '💎 Реферальна', icon: <PeopleIcon/>, children: [
    { text: '⚙️ Налаштування', path: '/referrals' },
    { text: '💰 Доходи', path: '/referral-earnings' },
  ]},
  { text: '📊 Аналітик метрик', icon: <PeopleIcon/>, path: '/analyst' },
  { text: '📩 Запити Trial', icon: <PeopleIcon/>, path: '/trial-requests' },
  { text: '🎭 Фейкові юзери', icon: <PeopleIcon/>, path: '/fake-users' },
  { text: '🎰 Конкурси', icon: <PeopleIcon/>, path: '/contests' },
  { text: '🎯 Утримання', icon: <PeopleIcon/>, path: '/retention' },
  { text: 'Відео', icon: <VideoLibraryIcon/>, path: '/videos' },
  { text: 'Фінанси', icon: <AccountBalanceWalletIcon/>, path: '/finance' },
  { text: '💵 Пакети', icon: <AccountBalanceWalletIcon/>, path: '/packages' },
  { text: 'Витрати/Дохід', icon: <BarChartIcon/>, path: '/costs' },
  { text: 'Фін. облік', icon: <CalculateIcon/>, path: '/expenses' },
  { text: 'Система', icon: <MonitorIcon/>, path: '/system' },
  { text: '🧳 Резервна копія', icon: <MonitorIcon/>, path: '/backup' },
  { text: '📱 Наші APP', icon: <AppsIcon/>, children: [
    { text: '💎 PlayLanguage', path: '/apps/playlanguage' },
    { text: '🔮 ZODIAQ', path: '/apps/zodiaq' },
    { text: '🖐 Хіромантія', path: '/apps/chiromancy' },
    { text: '🔢 NUMERIQ', path: '/apps/numeriq' },
    { text: '🎧 Музика редактора', path: '/apps/videoeditor-music' },
  ]},
  { text: '🤖 Аватар', icon: <VideoLibraryIcon/>, path: '/avatar' },
  { text: "🧠 Пам’ять АІ", icon: <PsychologyIcon/>, path: "/ai-memory" },
  { text: "🔑 Безшовна сесія", icon: <KeyIcon/>, path: "/seamless-session" },
  { text: 'Налаштування', icon: <SettingsIcon/>, path: '/settings' },
  // [D-734] лише в кінець: useMenuOrder пам'ятає порядок OWNER номерами пунктів - вставка посередині його переплутала б
  { text: '📲 Хто кого привів', icon: <PeopleIcon/>, path: '/app-invites' },
];

// [D-423] Пункти, які демо-режим прибирає з меню. Звіряємось із написом:
// порядок пунктів OWNER міняє перетягуванням, тож індекси тут ненадійні.
const HIDE_IN_DEMO = ['🎭 Фейкові юзери', 'Чорний список'];

function useMenuOrder() {
  const saved = localStorage.getItem('menu_order');
  let order = saved ? JSON.parse(saved) : DEFAULT_MENU.map((_,i)=>i);
  if (saved) {
    const allIndices = DEFAULT_MENU.map((_,i)=>i);
    const missing = allIndices.filter(i => !order.includes(i));
    if (missing.length > 0) order = [...order, ...missing];
  }
  const [menuOrder, setMenuOrder] = React.useState(order);
  // 🔴 Саме useDemo, а не isDemo: разове читання не перемалювало б меню
  // після натискання «ока» — пункти зникали б лише після перезавантаження.
  const demoHide = useDemo();
  const menuItems = menuOrder.map(i => DEFAULT_MENU[i]).filter(Boolean)
    .filter(it => !(demoHide && HIDE_IN_DEMO.includes(it.text)));
  const saveOrder = (newOrder) => { setMenuOrder(newOrder); localStorage.setItem('menu_order', JSON.stringify(newOrder)); };
  return { menuItems, menuOrder, saveOrder };
}

function Sidebar({ onLogout, open, onClose, isMobile, menuItems, onDragEnd, menuBg, menuText }) {
  const demoOn = useDemo();
  const [openGroups, setOpenGroups] = React.useState({});
  const [counts, setCounts] = React.useState({});
  React.useEffect(() => {
    const tok = localStorage.getItem('dashboard_token');
    const load = () => {
      Promise.all([
        fetch('/api/dashboard/notifications-counts', { headers: { Authorization: 'Bearer ' + tok } }).then(r => r.json()).catch(() => ({})),
        fetch('/api/dashboard/zodiaq/badges', { headers: { Authorization: 'Bearer ' + tok } }).then(r => r.json()).catch(() => ({})),
        fetch('/api/dashboard/chiromancy/badges', { headers: { Authorization: 'Bearer ' + tok } }).then(r => r.json()).catch(() => ({})),
        // 🔢 2026-09-11: NUMERIQ — нові юзери (раніше цей пункт меню не мав бейджа взагалі)
        fetch('/api/dashboard/numeriq/badges', { headers: { Authorization: 'Bearer ' + tok } }).then(r => r.json()).catch(() => ({}))
      ]).then(([a, b, c, d]) => setCounts({ ...(a || {}), zodiaq: (b && b.zodiaq) || 0, chiromancy: (c && c.chiromancy) || 0, numeriq: (d && d.numeriq) || 0 })).catch(() => {});
    };
    load();
    const i = setInterval(load, 30000);
    return () => clearInterval(i);
  }, []);
  const PATH_TO_COUNT = {
    '/users': 'pending_withdrawals',
    '/trial-requests': 'trial_requests',
    '/antifraud': 'antifraud',
    '/antihacker': 'antihacker',
    '/security': 'security',
    '/security/playlanguage': 'security',
    '/receipts': 'receipts',
    '/contests': 'contests',
    '/blacklist': 'blacklist',
    '/apps/playlanguage': 'playlanguage_reports',
    '/avatar': 'avatar_verify',
  };
  const getCount = (path) => {
    // 💎 D-285e: над «PlayLanguage» — скарги + невідповідані звернення підтримки
    // 💎 2026-09-11: + НОВІ ЮЗЕРИ. Гасне, коли OWNER відкрив обидві вкладки —
    // «👥 Users» і «🔗 Хто привів» (у кожної своя мітка «прочитано»).
    // [МЕДІА УНІКАЛІЗАТОР] 2026-09-11: над «Користувачі» — заявки на вивід + НОВІ ЮЗЕРИ
    if (path === '/users') return (counts.pending_withdrawals || 0) + (counts.media_new_users || 0);
    // [D-448] нові юзери партнерів — над «Партнери», не над «Користувачі»
    // [D-450] + неопрацьовані оплати юзерів партнерів
    if (path === '/partners') return (counts.partner_new_users || 0) + (counts.partner_sales_pending || 0);
    if (path === '/apps/playlanguage') return (counts.playlanguage_reports || 0) + (counts.playlanguage_support || 0) + (counts.playlanguage_new_any || 0);
    if (path === '/apps/zodiaq') return (counts.zodiaq_support || 0) + (counts.zodiaq || 0);
    if (path === '/apps/chiromancy') return (counts.chiromancy || 0);
    if (path === '/apps/numeriq') return (counts.numeriq || 0);
    // 🤖 2026-09-11: + нові юзери Аватара (вперше відкрили міні-апку)
    if (path === '/avatar') return (counts.avatar_verify || 0) + (counts.avatar_video_pending || 0) + (counts.avatar_new_users || 0);
    const k = PATH_TO_COUNT[path];
    return k ? counts[k] || 0 : 0;
  };
  const location = useLocation();
  const [opacity, setOpacity] = React.useState(() => parseFloat(localStorage.getItem('menu_opacity') || '1'));
  React.useEffect(() => {
    const handler = () => setOpacity(parseFloat(localStorage.getItem('menu_opacity') || '1'));
    window.addEventListener('menu_opacity_changed', handler);
    return () => window.removeEventListener('menu_opacity_changed', handler);
  }, []);
  const [logo, setLogo] = React.useState(localStorage.getItem('dashboard_logo') || null);
  React.useEffect(() => {
    const tok = localStorage.getItem('dashboard_token');
    fetch('/api/settings/logo', { headers: { Authorization: 'Bearer ' + tok } })
      .then(r => r.json()).then(d => { if (d.path) { const u = d.web || d.path; setLogo(u); localStorage.setItem('dashboard_logo', u); } });   // [D-649] легка копія з кешем, не ?t=зараз
  }, []);
  return (
    <Drawer variant={isMobile ? "temporary" : "permanent"}
      open={isMobile ? open : true}
      onClose={onClose}
      ModalProps={{ keepMounted: true }} sx={{ width: DRAWER_WIDTH, flexShrink: 0, '& .MuiDrawer-paper': { width: DRAWER_WIDTH, bgcolor: menuBg || `rgba(26,26,46,${opacity})`, backdropFilter: 'blur(10px)', color: menuText || '#fff' } }}>
      <Box sx={{ p: 2, borderBottom: '1px solid #333' }}>
        {logo ? <img src={logo} alt="logo" style={{ height:48, objectFit:'contain', maxWidth:'100%' }}/> :
          <Typography variant="h6" sx={{ color: 'var(--ac1, #7c4dff)', fontWeight: 'bold' }}>📊 Media Bot</Typography>}
        <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <Typography variant="caption" sx={{ color: '#aaa' }}>Dashboard</Typography>
          <Box onClick={() => window.location.reload()} sx={{ cursor:'pointer', color:'#555', fontSize:16, userSelect:'none', p:0.3, borderRadius:1, '&:hover':{color:'var(--ac1, #7c4dff)'} }} title="Оновити">🔄</Box>
          {/* [D-423] Демо-режим: показати дашборд сторонньому, не показуючи
              внутрішньої кухні. Стан лише в цьому браузері — на сервер
              нічого не йде, іншим нічого не змінюється. */}
          <Box onClick={() => setDemo(!isDemo())}
               sx={{ cursor:'pointer', fontSize:16, userSelect:'none', p:0.3,
                     borderRadius:1, opacity: demoOn ? 1 : .45,
                     color: demoOn ? '#00e5ff' : '#555',
                     '&:hover': { opacity: 1 } }}
               title={demoOn ? 'Демо-режим увімкнено — частину даних приховано' : 'Демо-режим: приховати чутливі дані'}>
            {demoOn ? '🙈' : '👁'}
          </Box>
        </Box>
      </Box>
      <List sx={{ flexGrow: 1 }}>
        {menuItems.map((item, idx) => {
          if (item.children) {
            const isOpen = openGroups[item.text] || false;
            const childActive = item.children.some(ch => ch.path === location.pathname);
            return (
              <React.Fragment key={item.text}>
                <ListItem onClick={() => setOpenGroups({...openGroups, [item.text]: !isOpen})}
                  sx={{ color: childActive ? 'var(--ac1, #7c4dff)' : '#ccc', cursor:'pointer', bgcolor: (item.children.reduce((s, ch) => s + getCount(ch.path), 0) > 0) ? 'rgba(255,45,45,0.25)' : (childActive ? 'rgba(var(--ac1rgb, 124, 77, 255),0.1)' : 'transparent'), borderRadius: 1, mx: 1, mb: 0.5, '&:hover': { bgcolor: 'rgba(var(--ac1rgb, 124, 77, 255),0.15)' }}}>
                  <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: 14 }}/>
                  {item.children && item.children.reduce((s, ch) => s + getCount(ch.path), 0) > 0 && <Badge badgeContent={item.children.reduce((s, ch) => s + getCount(ch.path), 0)} color="error" sx={{ mr: 1, '& .MuiBadge-badge': { right: 0, top: 0 } }}/>}
                  <Typography sx={{color:'#888', fontSize:12}}>{isOpen ? '▼' : '▶'}</Typography>
                </ListItem>
                {isOpen && item.children.map(ch => (
                  <ListItem key={ch.path} component={Link} to={ch.path} onClick={() => { if (isMobile && onClose) onClose(); }} sx={{ color: location.pathname === ch.path ? 'var(--ac1, #7c4dff)' : '#aaa', bgcolor: getCount(ch.path) > 0 ? 'rgba(255,45,45,0.25)' : (location.pathname === ch.path ? 'rgba(var(--ac1rgb, 124, 77, 255),0.1)' : 'transparent'), borderRadius: 1, mx: 1, mb: 0.3, pl: 5, '&:hover': { bgcolor: 'rgba(var(--ac1rgb, 124, 77, 255),0.15)' }}}>
                    <ListItemText primary={ch.text} primaryTypographyProps={{ fontSize: 13 }}/>
                    {getCount(ch.path) > 0 && <Badge badgeContent={getCount(ch.path)} color="error" sx={{ ml: 1, '& .MuiBadge-badge': { right: -3, top: 0 } }}/>}
                  </ListItem>
                ))}
              </React.Fragment>
            );
          }
          return (
          <ListItem key={item.path} component={Link} to={item.path}
            draggable onDragStart={() => window._dragFrom=idx}
            onDragOver={e => e.preventDefault()}
            onDrop={() => { if (onDragEnd && window._dragFrom!==undefined) onDragEnd(window._dragFrom, idx); }}
            onClick={() => { if (isMobile && onClose) onClose(); }}
            sx={{ color: location.pathname === item.path ? 'var(--ac1, #7c4dff)' : '#ccc',
                  bgcolor: getCount(item.path) > 0 ? 'rgba(255,45,45,0.25)' : (location.pathname === item.path ? 'rgba(var(--ac1rgb, 124, 77, 255),0.1)' : 'transparent'),
                  borderRadius: 1, mx: 1, mb: 0.5, '&:hover': { bgcolor: 'rgba(var(--ac1rgb, 124, 77, 255),0.15)' },
                  cursor: 'grab' }}>
            <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: 14 }}/>
            {getCount(item.path) > 0 && <Badge badgeContent={getCount(item.path)} color="error" sx={{ ml: 1, '& .MuiBadge-badge': { right: -3, top: 0 } }}/>}
          </ListItem>
          );
        })}
      </List>
      <List onClick={() => { if (isMobile && onClose) onClose(); }}>
        <ListItem onClick={onLogout} sx={{ color: '#f44336', cursor: 'pointer', mx: 1, borderRadius: 1, '&:hover': { bgcolor: 'rgba(244,67,54,0.1)' } }}>
          <ListItemIcon sx={{ color: '#f44336', minWidth: 36 }}><LogoutIcon/></ListItemIcon>
          <ListItemText primary="Вийти" primaryTypographyProps={{ fontSize: 14 }}/>
        </ListItem>
      </List>
    </Drawer>
  );
}

export default function App() {
  const { menuItems, menuOrder, saveOrder } = useMenuOrder();
  const [bgImage, setBgImage] = React.useState(null);
  const [menuBg, setMenuBg] = React.useState(() => localStorage.getItem('menu_bg') || '#1a1a2e');
  const [menuText, setMenuText] = React.useState(() => localStorage.getItem('menu_text') || '#ffffff');
  React.useEffect(() => {
    const handler = () => {
      setMenuBg(localStorage.getItem('menu_bg') || '#1a1a2e');
      setMenuText(localStorage.getItem('menu_text') || '#ffffff');
    };
    window.addEventListener('theme_changed', handler);
    return () => window.removeEventListener('theme_changed', handler);
  }, []);
  const [bgSize, setBgSize] = React.useState(() => localStorage.getItem('bg_size') || 'cover');
  const [bgPosX, setBgPosX] = React.useState(() => parseInt(localStorage.getItem('bg_pos_x') || '50'));
  const [bgPosY, setBgPosY] = React.useState(() => parseInt(localStorage.getItem('bg_pos_y') || '50'));
  React.useEffect(() => {
    const handler = () => {
      setBgSize(localStorage.getItem('bg_size') || 'cover');
      setBgPosX(parseInt(localStorage.getItem('bg_pos_x') || '50'));
      setBgPosY(parseInt(localStorage.getItem('bg_pos_y') || '50'));
    };
    window.addEventListener('bg_settings_changed', handler);
    return () => window.removeEventListener('bg_settings_changed', handler);
  }, []);
  const [cardOpacity, setCardOpacity] = React.useState(() => parseFloat(localStorage.getItem('card_opacity') || '1'));
  React.useEffect(() => {
    const handler = () => {
      const val = parseFloat(localStorage.getItem('card_opacity') || '1');
      setCardOpacity(val);
      document.documentElement.style.setProperty('--card-opacity', val);
    };
    window.addEventListener('card_opacity_changed', handler);
    return () => window.removeEventListener('card_opacity_changed', handler);
  }, []);
  React.useEffect(() => {
    document.documentElement.style.setProperty('--card-opacity', cardOpacity);
  }, [cardOpacity]);
  React.useEffect(() => {
    const tok = localStorage.getItem('dashboard_token');
    if (!tok) return;
    fetch('/api/settings/background', { headers: { Authorization: 'Bearer ' + tok } })
      .then(r => r.json()).then(d => { if (d.path) setBgImage(d.web || d.path); }).catch(()=>{});   // [D-649] легка копія з кешем
  }, []);
  const [dragIdx, setDragIdx] = React.useState(null);
  const onDragEnd = (fromIdx, toIdx) => {
    const newOrder = [...menuOrder];
    const [moved] = newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, moved);
    saveOrder(newOrder);
  };
  const [token, setToken] = useState(localStorage.getItem('dashboard_token'));
  const isMobile = useMediaQuery('(max-width:900px)');
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
    }
  }, [token]);

  const handleLogin = (t) => {
    setToken(t);
    axios.defaults.headers.common['Authorization'] = 'Bearer ' + t;
  };

  const handleLogout = () => {
    localStorage.removeItem('dashboard_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
  };

  if (!token) return <LoginPage onLogin={handleLogin}/>;

  return (
    <BrowserRouter>
      <Box sx={{ display: 'flex', bgcolor: '#0f0f1a', minHeight: '100vh', isolation: 'isolate' }}>
        {/* [D-649] фон - окремим нерухомим шаром позаду: вигляд як з background-attachment: fixed, але телефон не перемальовує картинку на кожен кадр прокрутки */}
        {bgImage && <Box aria-hidden sx={{ position: 'fixed', inset: 0, zIndex: -1, pointerEvents: 'none', backgroundImage: `url(${bgImage})`, backgroundSize: bgSize, backgroundPosition: `${bgPosX}% ${bgPosY}%` }} />}
        <CssBaseline/>
      {/* 2026-07-02: у дашборді немає MUI-теми -> Select/Menu попапи рендерились
          світлою темою і були нечитабельні. Глобальний дарк для всіх попапів. */}
      <GlobalStyles styles={{
        '.MuiMenu-paper, .MuiPopover-paper, .MuiAutocomplete-paper, .MuiDialog-paper': {
          backgroundColor: '#1a1a2e !important', color: '#fff !important',
          border: '1px solid #3a3a5e',
        },
        '.MuiMenuItem-root': { color: '#e8e8f0 !important' },
        '.MuiMenuItem-root:hover': { backgroundColor: 'rgba(var(--ac1rgb, 124, 77, 255),0.18) !important' },
        '.MuiMenuItem-root.Mui-selected': { backgroundColor: 'rgba(var(--ac1rgb, 124, 77, 255),0.3) !important' },
        '.MuiAutocomplete-option': { color: '#e8e8f0 !important' },
      }} />
        <Sidebar onLogout={handleLogout} open={drawerOpen} onClose={() => setDrawerOpen(false)} isMobile={isMobile} menuItems={menuItems} onDragEnd={onDragEnd} menuBg={menuBg} menuText={menuText}/>
        {/* [МЕДІА УНІКАЛІЗАТОР] фікс подвійного відступу: permanent Drawer вже резервує DRAWER_WIDTH у flex —
            прибрано зайвий ml/width (був +220px порожнечі); лишається чистий p:3 (марджин 3) */}
        <Box component="main" sx={{ flexGrow: 1, p: isMobile ? 1 : 3, minWidth: 0, overflowX: 'hidden' }}>
          {isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 1, bgcolor: 'rgba(26,26,46,0.7)', borderRadius: 1 }}>
              <Box onClick={() => setDrawerOpen(true)} sx={{ cursor: 'pointer', color: 'var(--ac1, #7c4dff)', fontSize: 32, mr: 2, userSelect: 'none' }}>☰</Box>
              <Typography variant="subtitle1" sx={{ color: 'var(--ac1, #7c4dff)', fontWeight: 'bold' }}>📊 Media Bot</Typography>
            </Box>
          )}
          <Routes>
            <Route path="/" element={<StatsPage/>}/>
            <Route path="/users" element={<UsersPage/>}/>
            <Route path="/referrals" element={<ReferralSettingsPage/>}/>
            <Route path="/referral-earnings" element={<ReferralEarningsPage/>}/>
            <Route path="/trial-requests" element={<TrialRequestsPage/>}/>
            <Route path="/fake-users" element={<FakeUsersPage/>}/>
            <Route path="/antifraud" element={<AntifraudPage/>}/>
            <Route path="/antihacker" element={<AntihackerPage/>}/>
            <Route path="/analyst" element={<AnalystPage/>}/>
            <Route path="/contests" element={<ContestsPage/>}/>
            <Route path="/retention" element={<RetentionPage/>}/>
            <Route path="/users/:id" element={<UserDetailPage/>}/>
            <Route path="/videos" element={<VideosPage/>}/>
            <Route path="/finance" element={<FinancePage/>}/>
            <Route path="/costs" element={<CostsPage/>}/>
            <Route path="/queue" element={<QueuePage/>}/>
            <Route path="/app-invites" element={<AppInvitesPage/>}/>   {/* [D-734] */}
            <Route path="/hyper" element={<HyperSettingsPage/>}/>   {/* [D-321] */}
            <Route path="/partners" element={<PartnersPage/>}/>   {/* [D-409] */}
            <Route path="/our-apps" element={<OurAppsPage/>}/>   {/* [D-420] */}
            <Route path="/support" element={<SupportPage/>}/>
            <Route path="/receipts" element={<ReceiptsPage/>}/>
            <Route path="/blacklist" element={<BlacklistPage/>}/>
            <Route path="/security" element={<SecurityPage/>}/>
            <Route path="/security/playlanguage" element={<PlayLanguageSecurityPage/>}/>
            <Route path="/expenses" element={<ExpensesPage/>}/>
            <Route path="/system" element={<SystemPage/>}/>
            <Route path="/avatar" element={<AvatarPage/>}/>
            <Route path="/settings" element={<SettingsPage/>}/>
            <Route path="/packages" element={<PackagesPage/>}/>
            <Route path="/apps/playlanguage" element={<PlayLanguagePage/>}/>
            <Route path="/apps/zodiaq" element={<ZodiaqPage/>}/>
            <Route path="/apps/chiromancy" element={<ChiromancyPage/>}/>
            <Route path="/apps/numeriq" element={<NumeriqPage/>}/>
            <Route path="/apps/videoeditor-music" element={<VideoEditorMusicPage/>}/>
            <Route path="/apps/studio-music" element={<StudioMusicPage/>}/>
            <Route path="/secrets" element={<SecretsPage/>}/>
            <Route path="/backup" element={<BackupPage/>}/>
            <Route path="/ai-memory" element={<AIMemoryPage/>}/>
            <Route path="/seamless-session" element={<SeamlessSessionPage/>}/>
          </Routes>
        </Box>
      </Box>
    </BrowserRouter>
  );
}
// rebuild 1777710259
